import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { adminApi } from '../../services/api';
import { Navigate } from 'react-router-dom';

const AdminPage = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await adminApi.getAllUsers();
        if (response.success) {
          setUsers(response.data);
          setFilteredUsers(response.data);
        } else {
          setError('Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if user is admin
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(
        user => 
          user.displayName?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setCreditAmount(0);
  };

  const handleAddCredits = async () => {
    if (!selectedUser || !creditAmount || creditAmount <= 0) return;

    try {
      // Use the correct user ID property (firebaseUid) instead of uid
      const userId = selectedUser.firebaseUid || selectedUser.uid;
      if (!userId) {
        alert('Invalid user ID');
        return;
      }

      const response = await adminApi.adjustUserCredits(userId, creditAmount);
      if (response.success) {
        // Update the user's credits in the local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            (user.firebaseUid === userId || user.uid === userId)
              ? { ...user, credits: (user.credits || 0) + Number(creditAmount) }
              : user
          )
        );
        setSelectedUser(prev => ({
          ...prev,
          credits: (prev.credits || 0) + Number(creditAmount)
        }));
        alert(`Successfully added ${creditAmount} credits to ${selectedUser.displayName}`);
        setCreditAmount(0);
      } else {
        alert('Failed to add credits');
      }
    } catch (err) {
      console.error('Error adding credits:', err);
      alert(`Error adding credits: ${err.message}`);
    }
  };

  const handleRemoveCredits = async () => {
    if (!selectedUser || !creditAmount || creditAmount <= 0) return;

    try {
      // Use the correct user ID property (firebaseUid) instead of uid
      const userId = selectedUser.firebaseUid || selectedUser.uid;
      if (!userId) {
        alert('Invalid user ID');
        return;
      }

      const response = await adminApi.adjustUserCredits(userId, -creditAmount);
      if (response.success) {
        // Update the user's credits in the local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            (user.firebaseUid === userId || user.uid === userId)
              ? { ...user, credits: Math.max(0, (user.credits || 0) - Number(creditAmount)) }
              : user
          )
        );
        setSelectedUser(prev => ({
          ...prev,
          credits: Math.max(0, (prev.credits || 0) - Number(creditAmount))
        }));
        alert(`Successfully removed ${creditAmount} credits from ${selectedUser.displayName}`);
        setCreditAmount(0);
      } else {
        alert('Failed to remove credits');
      }
    } catch (err) {
      console.error('Error removing credits:', err);
      alert(`Error removing credits: ${err.message}`);
    }
  };

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/profile" />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-muted-foreground">Loading user data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-3xl font-bold mb-8 text-foreground">Admin Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-6">
        {/* User List */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-foreground">User Management</h2>
            <div className="mb-4">
              <Input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="overflow-y-auto max-h-[500px] border border-border rounded">
              {filteredUsers.length > 0 ? (
                <div className="divide-y divide-border">
                  {filteredUsers.map((user) => (
                    <div 
                      key={user.firebaseUid || user.uid} 
                      className={`p-3 cursor-pointer hover:bg-accent transition-colors ${
                        (selectedUser?.firebaseUid === user.firebaseUid || 
                         selectedUser?.uid === user.uid) ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => handleUserSelect(user)}
                    >
                      <p className="font-medium text-foreground">{user.displayName || 'Unknown User'}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      <p className="text-sm text-primary">Credits: {user.credits || 0}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="p-4 text-center text-muted-foreground">
                  {searchQuery ? 'No users found matching your search' : 'No users found'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Details & Credit Management */}
        <Card>
          <CardContent className="p-6">
            {selectedUser ? (
              <div>
                <h2 className="text-xl font-semibold mb-4 text-foreground">User Details</h2>
                <div className="mb-6 p-4 bg-primary/10 rounded-lg">
                  <h3 className="font-medium text-lg text-foreground">{selectedUser.displayName || 'Unknown User'}</h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                  <p className="mt-2"><span className="text-muted-foreground">User ID:</span> {selectedUser.firebaseUid || selectedUser.uid}</p>
                  <p><span className="text-muted-foreground">Current Credits:</span> <span className="font-medium text-primary">{selectedUser.credits || 0}</span></p>
                  <p><span className="text-muted-foreground">Account Status:</span> {selectedUser.isActive ? 'Active' : 'Inactive'}</p>
                </div>

                <div className="mb-6">
                  <h3 className="font-medium mb-2 text-foreground">Adjust Credits</h3>
                  <div className="flex gap-2 mb-4">
                    <Input
                      type="number"
                      placeholder="Credit amount"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(Number(e.target.value))}
                      className="flex-1"
                      min="0"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAddCredits}
                      disabled={!creditAmount || creditAmount <= 0}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Add Credits
                    </Button>
                    <Button 
                      onClick={handleRemoveCredits}
                      disabled={!creditAmount || creditAmount <= 0}
                      variant="destructive"
                    >
                      Remove Credits
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Select a user to view details and manage credits</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPage; 